import { Box, CloseButton, Flex, Image, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { formatFileSize } from '../../helpers'
import { ActionButton } from '../ActionButton'
import { Icon } from '../Icon'
import { fileExtension, isPreviewableImage } from './fileTypes'

/**
 * Como os arquivos escolhidos aparecem abaixo do controle.
 *
 * - `list` é a linha com nome e tamanho, para documento e planilha — o que a entrega de trabalho
 *   e o aproveitamento de estudos já faziam, cada um com a sua cópia.
 * - `thumbnail` é o ladrilho com a miniatura, para anexo e comprovante, onde reconhecer o print
 *   pela imagem vale mais do que ler `Captura de tela 2026-09-14 às 10.13.22.png`.
 */
export type TFilePreview = 'list' | 'thumbnail'

interface IFilePreviewListProps {
  files: File[]
  onRemove?: (index: number) => void
  variant?: TFilePreview
  isDisabled?: boolean
}

/**
 * Object URLs das imagens da lista, criadas e revogadas em bloco.
 *
 * As duas telas de suporte carregavam um estado paralelo de previews indexado por posição, ao lado
 * do array de arquivos — duas listas para manter em sincronia, e a remoção tinha que mexer nas
 * duas. Aqui a URL é derivada do arquivo, então não há o que dessincronizar.
 */
function useObjectUrls(files: File[]): (string | null)[] {
  const [urls, setUrls] = useState<(string | null)[]>([])

  useEffect(() => {
    const created = files.map((file) => (isPreviewableImage(file) ? URL.createObjectURL(file) : null))
    setUrls(created)

    return () => created.forEach((url) => url && URL.revokeObjectURL(url))
  }, [files])

  return urls
}

export default function FilePreviewList({ files, onRemove, variant = 'list', isDisabled }: IFilePreviewListProps) {
  const urls = useObjectUrls(files)

  if (files.length === 0) return null

  if (variant === 'thumbnail') {
    return (
      <Flex mt={2} wrap="wrap" gap={2}>
        {files.map((file, index) => (
          <Box
            key={`${file.name}-${file.size}`}
            position="relative"
            w="90px"
            h="70px"
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.200"
            bg={urls[index] ? 'transparent' : 'gray.100'}
          >
            {urls[index] ? (
              <Image src={urls[index] as string} alt={file.name} w="100%" h="100%" objectFit="cover" />
            ) : (
              <Flex direction="column" align="center" justify="center" w="100%" h="100%" color="gray.600">
                <Icon name="lni lni-empty-file" size="xl" />
                <Text fontSize="xs" mt={1} textTransform="uppercase">
                  {fileExtension(file.name) || 'arquivo'}
                </Text>
              </Flex>
            )}

            {onRemove && !isDisabled && (
              <CloseButton
                aria-label={`Remover ${file.name}`}
                size="sm"
                position="absolute"
                top="1px"
                right="1px"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
                onClick={() => onRemove(index)}
              />
            )}
          </Box>
        ))}
      </Flex>
    )
  }

  return (
    <>
      {files.map((file, index) => (
        <Flex key={`${file.name}-${file.size}`} align="center" gap={2} mt={2} fontSize="sm">
          {onRemove && (
            <ActionButton
              action="delete"
              label={`Remover ${file.name}`}
              size="xs"
              variant="outline"
              mr={0}
              isDisabled={isDisabled}
              onClick={() => onRemove(index)}
            />
          )}
          <Text noOfLines={1}>{file.name}</Text>
          <Text color="gray.500" flexShrink={0}>
            {formatFileSize(file.size)}
          </Text>
        </Flex>
      ))}
    </>
  )
}
